/**
 * Which parts belong to the same physical scaffold.
 *
 * Standards are the vertices and beams, diagonals and brackets the edges; a
 * scaffold is one connected component of that graph. Everything else — decks,
 * guardrails, toe boards, stairs — belongs to the component holding the
 * coordinates it references. Two runs that share no beam are two components,
 * so one can be moved without disturbing the other.
 *
 * The component is keyed on coordinates rather than legs so that a beam with a
 * free end carries that end along with it.
 */

import { bayEdges, kXZ } from "./geometry.ts";
import type { Leg, PartRef, Point, ScaffoldModel } from "./types.ts";
import { isUtvStair } from "./types.ts";

/** How far off a transom a UTV stair's hook may sit and still count as on it. */
const STAIR_ATTACH_TOLERANCE = 0.06;

export type ScaffoldGroup = Readonly<{
  /** Every coordinate in the component, including free beam ends. */
  points: ReadonlySet<string>;
  legKeys: ReadonlySet<string>;
  beams: ReadonlySet<number>;
  diags: ReadonlySet<number>;
  decks: ReadonlySet<number>;
  stairs: ReadonlySet<number>;
  ladders: ReadonlySet<number>;
  rails: ReadonlySet<number>;
  toes: ReadonlySet<number>;
  brackets: ReadonlySet<number>;
  boards: ReadonlySet<number>;
}>;

const at = (p: Point): string => kXZ(p.x, p.z);

/** Is `point` on the segment a–b, within `tolerance` metres? */
function onSegment(a: Point, b: Point, point: Point, tolerance: number): boolean {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-6) {
    return false;
  }

  const t = ((point.x - a.x) * dx + (point.z - a.z) * dz) / (len * len);
  if (t < -0.02 || t > 1.02) {
    return false;
  }

  return Math.abs((point.x - a.x) * (-dz / len) + (point.z - a.z) * (dx / len)) <= tolerance;
}

/** Coordinate adjacency implied by beams and diagonals. */
function buildAdjacency(model: ScaffoldModel): Map<string, string[]> {
  const adjacency = new Map<string, string[]>();
  const link = (u: string, v: string): void => {
    const existing = adjacency.get(u);
    if (existing) {
      existing.push(v);
    } else {
      adjacency.set(u, [v]);
    }
  };

  for (const beam of model.beams) {
    link(at(beam.a), at(beam.b));
    link(at(beam.b), at(beam.a));
  }
  for (const diag of model.diags) {
    link(at(diag.a), at(diag.b));
    link(at(diag.b), at(diag.a));
  }
  for (const bracket of model.brackets) {
    link(at(bracket.a), at(bracket.b));
    link(at(bracket.b), at(bracket.a));
  }

  return adjacency;
}

/** The UTV stair's lower transom, so the stair travels with the beams it hangs on. */
function utvAnchorPoints(model: ScaffoldModel, index: number): Point[] {
  const stair = model.stairs[index];
  if (!stair || !isUtvStair(stair)) {
    return [];
  }

  const beam = model.beams.find(
    (b) => b.p === stair.p && onSegment(b.a, b.b, stair.o, STAIR_ATTACH_TOLERANCE),
  );

  return beam ? [beam.a, beam.b] : [];
}

/** The edge a loose board rests on at its first end, so the board travels with the beams under it. */
function boardAnchorPoints(model: ScaffoldModel, index: number): Point[] {
  const board = model.boards[index];
  if (!board) {
    return [];
  }

  const edge = bayEdges(model).find(
    (candidate) =>
      candidate.p === board.p && onSegment(candidate.a, candidate.b, board.a, STAIR_ATTACH_TOLERANCE),
  );

  return edge ? [edge.a, edge.b] : [];
}

/** Flood-fill from `seeds` across the beam/diagonal graph. */
export function groupFromPoints(model: ScaffoldModel, seeds: readonly Point[]): ScaffoldGroup {
  const adjacency = buildAdjacency(model);
  const points = new Set<string>();
  const queue: string[] = [];

  for (const seed of seeds) {
    const key = at(seed);
    if (!points.has(key)) {
      points.add(key);
      queue.push(key);
    }
  }

  while (queue.length > 0) {
    const key = queue.pop();
    if (key === undefined) {
      break;
    }
    for (const next of adjacency.get(key) ?? []) {
      if (!points.has(next)) {
        points.add(next);
        queue.push(next);
      }
    }
  }

  const anyIn = (...ps: Point[]): boolean => ps.some((p) => points.has(at(p)));
  const collect = <T>(items: readonly T[], test: (item: T, index: number) => boolean): Set<number> => {
    const out = new Set<number>();
    items.forEach((item, index) => {
      if (test(item, index)) {
        out.add(index);
      }
    });

    return out;
  };

  const legKeys = new Set<string>();
  for (const key of points) {
    if (model.legs.has(key)) {
      legKeys.add(key);
    }
  }

  return {
    points,
    legKeys,
    beams: collect(model.beams, (b) => anyIn(b.a, b.b)),
    diags: collect(model.diags, (d) => anyIn(d.a, d.b)),
    decks: collect(model.decks, (d) => anyIn(...d.c)),
    rails: collect(model.rails, (r) => anyIn(r.a, r.b)),
    toes: collect(model.toes, (t) => anyIn(t.a, t.b)),
    ladders: collect(model.ladders, (l) => anyIn(l.a, l.b)),
    stairs: collect(model.stairs, (s, index) =>
      isUtvStair(s) ? anyIn(...utvAnchorPoints(model, index)) : anyIn(s.a, s.b),
    ),
    brackets: collect(model.brackets, (k) => anyIn(k.a, k.b)),
    boards: collect(model.boards, (_, index) => anyIn(...boardAnchorPoints(model, index))),
  };
}

/** The coordinates a picked part sits on, used to seed the flood fill. */
export function anchorPoints(model: ScaffoldModel, ref: PartRef): Point[] {
  switch (ref.type) {
    case "leg": {
      const leg = model.legs.get(ref.key);
      return leg ? [{ x: leg.x, z: leg.z }] : [];
    }
    case "freeEnd":
      return [{ x: ref.x, z: ref.z }];
    case "beam":
      return endsOf(model.beams[ref.idx]);
    case "diag":
      return endsOf(model.diags[ref.idx]);
    case "ladder":
      return endsOf(model.ladders[ref.idx]);
    case "rail":
      return endsOf(model.rails[ref.idx]);
    case "toe":
      return endsOf(model.toes[ref.idx]);
    case "bracket":
      return endsOf(model.brackets[ref.idx]);
    case "board":
      return boardAnchorPoints(model, ref.idx);
    case "deck":
    case "plank":
      return model.decks[ref.idx]?.c.slice() ?? [];
    default: {
      const stair = model.stairs[ref.idx];
      if (!stair) {
        return [];
      }
      return isUtvStair(stair) ? utvAnchorPoints(model, ref.idx) : [stair.a, stair.b];
    }
  }
}

function endsOf(part: { a: Point; b: Point } | undefined): Point[] {
  return part ? [part.a, part.b] : [];
}

/** The whole scaffold a picked part belongs to, or null when it stands alone. */
export function groupFromPart(model: ScaffoldModel, ref: PartRef): ScaffoldGroup | null {
  const seeds = anchorPoints(model, ref);

  return seeds.length > 0 ? groupFromPoints(model, seeds) : null;
}

/** Does this group hold the part? Drives the hover highlight. */
export function groupHasPart(group: ScaffoldGroup, ref: PartRef): boolean {
  if (ref.type === "leg") {
    return group.legKeys.has(ref.key);
  }
  if (ref.type === "freeEnd") {
    return group.points.has(kXZ(ref.x, ref.z));
  }
  if (ref.type === "beam") {
    return group.beams.has(ref.idx);
  }
  if (ref.type === "diag") {
    return group.diags.has(ref.idx);
  }
  if (ref.type === "deck" || ref.type === "plank") {
    return group.decks.has(ref.idx);
  }
  if (ref.type === "rail") {
    return group.rails.has(ref.idx);
  }
  if (ref.type === "toe") {
    return group.toes.has(ref.idx);
  }
  if (ref.type === "ladder") {
    return group.ladders.has(ref.idx);
  }
  if (ref.type === "bracket") {
    return group.brackets.has(ref.idx);
  }
  if (ref.type === "board") {
    return group.boards.has(ref.idx);
  }

  return group.stairs.has(ref.idx);
}

/** How many standards the group holds — what the HUD reports. */
export function groupSize(group: ScaffoldGroup): number {
  return group.legKeys.size;
}

/**
 * The group's standards, as live references.
 *
 * A drag applies many small steps, and every step re-keys the leg map. Looking
 * the legs up by their original coordinate would therefore find nothing after
 * the first step — the standards would stop while the index-keyed beams kept
 * going. Resolve once, then move these objects.
 */
export function groupLegs(model: ScaffoldModel, group: ScaffoldGroup): Leg[] {
  const legs: Leg[] = [];

  for (const key of group.legKeys) {
    const leg = model.legs.get(key);
    if (leg) {
      legs.push(leg);
    }
  }

  return legs;
}

function totalMm(leg: { segments: readonly number[] }): number {
  return leg.segments.reduce((sum, mm) => sum + mm, 0);
}

/** Apply `move` to each listed index of `items`, skipping gaps. */
function shiftEach<T>(items: readonly T[], indices: ReadonlySet<number>, move: (item: T) => void): void {
  for (const index of indices) {
    const item = items[index];
    if (item) {
      move(item);
    }
  }
}

export type TranslateResult = Readonly<{
  /** Standards that landed on a standing one and were merged into it. */
  merged: number;
}>;

/**
 * Shift every part of the group by (dx, dz), in metres.
 *
 * `legs` must come from `groupLegs`, resolved once at the start of a drag.
 *
 * Legs drop their cached ground height so the next render re-solves it from
 * above; a leg that lands on a roof or a step then gets the right base jack
 * rather than a stale one cast from its old surface.
 *
 * Two standards cannot share a coordinate. While `commit` is false — during a
 * drag — a moving standard that lands on a standing one is simply not placed,
 * and reappears once the pointer moves on; nothing is destroyed by dragging
 * across another scaffold. On drop, `commit` resolves it for real and the
 * taller stack survives, so nothing silently gets shorter.
 */
export function translateGroup(
  model: ScaffoldModel,
  group: ScaffoldGroup,
  legs: readonly Leg[],
  dx: number,
  dz: number,
  commit = false,
): TranslateResult {
  const shift = (p: Point): Point => ({ x: +(p.x + dx).toFixed(3), z: +(p.z + dz).toFixed(3) });

  // Detach only entries that are these very legs, so a standing standard the
  // group is currently overlapping is never removed.
  for (const leg of legs) {
    const key = kXZ(leg.x, leg.z);
    if (model.legs.get(key) === leg) {
      model.legs.delete(key);
    }
  }

  let merged = 0;
  for (const leg of legs) {
    leg.x = +(leg.x + dx).toFixed(3);
    leg.z = +(leg.z + dz).toFixed(3);
    leg.gy = undefined;
    leg.baseP = undefined;

    const key = kXZ(leg.x, leg.z);
    const standing = model.legs.get(key);
    if (!standing) {
      model.legs.set(key, leg);
      continue;
    }

    merged++;
    if (commit && totalMm(leg) > totalMm(standing)) {
      model.legs.set(key, leg);
    }
  }

  shiftEach(model.beams, group.beams, (beam) => {
    beam.a = shift(beam.a);
    beam.b = shift(beam.b);
  });
  shiftEach(model.diags, group.diags, (diag) => {
    diag.a = shift(diag.a);
    diag.b = shift(diag.b);
  });
  shiftEach(model.decks, group.decks, (deck) => {
    deck.c = [shift(deck.c[0]), shift(deck.c[1]), shift(deck.c[2]), shift(deck.c[3])];
  });
  shiftEach(model.rails, group.rails, (rail) => {
    rail.a = shift(rail.a);
    rail.b = shift(rail.b);
  });
  shiftEach(model.toes, group.toes, (toe) => {
    toe.a = shift(toe.a);
    toe.b = shift(toe.b);
  });
  shiftEach(model.ladders, group.ladders, (ladder) => {
    ladder.a = shift(ladder.a);
    ladder.b = shift(ladder.b);
  });
  shiftEach(model.brackets, group.brackets, (bracket) => {
    bracket.a = shift(bracket.a);
    bracket.b = shift(bracket.b);
  });
  shiftEach(model.boards, group.boards, (board) => {
    board.a = shift(board.a);
    board.b = shift(board.b);
  });
  shiftEach(model.stairs, group.stairs, (stair) => {
    if (isUtvStair(stair)) {
      stair.o = shift(stair.o);
      return;
    }
    stair.a = shift(stair.a);
    stair.b = shift(stair.b);
  });

  return { merged };
}
